import './Referrals.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  DateRangePicker,
  Header,
  List,
  ListItem,
  Placeholder,
  ScrollView,
} from '$uikit';
import { roundNumber } from '$utils';
import { inviteActions, inviteSelector } from '$store/invite';
import { goTo, showModal } from '$navigation/helper';
import { useDesktop, useTranslator } from '$hooks';
import { GOLD_PROGRAM_PAGE, GOLD_PROGRAM_TELEGRAM } from '$core/Invite/Invite';
import { Loader, Table } from '$shared/components';
import { createColumnHelper } from '@tanstack/react-table';
import { InviteStatsRow } from '$store/invite/interface';
import { IcAdd28, IcAddCircle28, IcDropdown16, IcPlus28, IcPlusCircle28 } from '$assets';
import { formatNumber } from '$utils/number';
import { CellContext } from '@tanstack/table-core';

const columnHelper = createColumnHelper<InviteStatsRow>();

export const Referrals: FC = () => {
  const {
    isGold,
    invited,
    subscribed,
    profit,
    referralPercent,
    isStatsLoading,
    links,
    stats,
    statsDateRange,
    pagination,
  } = useSelector(inviteSelector);
  const t = useTranslator();
  const dispatch = useDispatch();
  const isDesktop = useDesktop();
  const [linkFilter, setLinkFilter] = useState<number[]>([]);

  useEffect(() => {
    dispatch(inviteActions.loadInviteStats());
    dispatch(inviteActions.loadInviteStatsTable());
  }, []);

  const [startDate, endDate] = statsDateRange;

  const getLinkById = useCallback(
    (id: number) => {
      return links && links.find((link) => link.id === id);
    },
    [links],
  );

  const preparedStats = useMemo(
    () =>
      stats.stats.filter(
        (item) =>
          (!linkFilter.length || linkFilter.includes(item.linkId)) &&
          getLinkById(item.linkId),
      ),
    [linkFilter, stats.stats, getLinkById],
  );

  const formatProfit = useCallback(
    (row: CellContext<InviteStatsRow, any>) => {
      const value = formatNumber(row.getValue());
      return isGold ? `€${value}` : `${value} TON`;
    },
    [isGold],
  );

  const columns = [
    columnHelper.accessor('linkId', {
      cell: (row) => (
        <div className="Referrals__rowCell__name">
          {getLinkById(row.getValue())?.name}
        </div>
      ),
      header: () => (
        <div className="Referrals__tableHeader__name">
          {t('referrals_table_header_name')}
        </div>
      ),
    }),
    columnHelper.accessor('views', {
      header: t('referrals_table_header_views'),
    }),
    columnHelper.accessor('joined', {
      header: t('referrals_table_header_joined'),
    }),
    columnHelper.accessor('payers', {
      header: t('referrals_table_header_payers'),
    }),
    columnHelper.accessor('eroticProfit', {
      cell: formatProfit,
      header: t('referrals_table_header_erotic_profit'),
    }),
    columnHelper.accessor('sponsorProfit', {
      cell: formatProfit,
      header: t('referrals_table_header_sponsor_profit'),
    }),
    columnHelper.accessor('tipsProfit', {
      cell: formatProfit,
      header: t('referrals_table_header_tips_profit'),
    }),
    columnHelper.accessor('messageProfit', {
      cell: formatProfit,
      header: t('referrals_table_header_message_profit'),
    }),
    columnHelper.accessor('totalProfit', {
      cell: formatProfit,
      header: t('referrals_table_header_total_profit'),
    }),
  ];

  const handleShowLinks = useCallback(() => {
    showModal('links');
  }, []);

  const handleCreateLink = useCallback(() => {
    showModal('create_link');
  }, []);

  const linkFilterValue = useMemo(() => {
    return links
      ?.filter((link) => linkFilter.includes(link.id))
      .map((link) => ({
        label: link.name,
        value: link.id,
      }));
  }, [linkFilter, links]);

  const linkFilterOptions = useMemo(
    () =>
      links?.map((link) => ({
        label: link.name,
        value: link.id,
      })),
    [links],
  );

  const handleEnableGold = useCallback(() => {
    showModal('enable_gold', false);
  }, []);

  return (
    <>
      <Header showBackButton>{t('referrals_title')}</Header>
      <ScrollView
        onLoadMore={() => dispatch(inviteActions.loadMoreStatsTable('statsTable'))}
        canLoadMore={pagination['statsTable'].nextFrom !== 0}
      >
        {isStatsLoading ? (
          <Placeholder>
            <Loader />
          </Placeholder>
        ) : (
          <div className="Referrals">
            <div className="Referrals__columns">
              <div className="Referrals__column">
                <List>
                  <ListItem
                    chevron
                    onClick={() => {
                      showModal('balance', true, { currency: 'eur' });
                    }}
                    indicator={
                      isGold ? '€' + roundNumber(profit) : roundNumber(profit) + ' TON'
                    }
                  >
                    {t('invite_stats_item_earnings')}
                  </ListItem>
                  <ListItem
                    chevron
                    onClick={() => showModal('referrals_invited')}
                    indicator={pagination['stats'].length.toString()}
                  >
                    {t('invite_stats_item_referrals')}
                  </ListItem>
                  <ListItem
                    chevron
                    onClick={() => showModal('referrals_subscribed')}
                    indicator={subscribed.length.toString()}
                  >
                    {t('invite_stats_item_subscribed')}
                  </ListItem>
                  <ListItem indicator={referralPercent + '%'}>
                    {t('invite_stats_item_percent')}
                    {/*Убрал по просьбе Никиты*/}
                    {/*{!isGold && (*/}
                    {/*  <Button*/}
                    {/*    size="extra-small"*/}
                    {/*    mode="secondary"*/}
                    {/*    onClick={handleEnableGold}*/}
                    {/*  >*/}
                    {/*    {t('invite_enable_gold_button')}*/}
                    {/*  </Button>*/}
                    {/*)}*/}
                  </ListItem>
                </List>
              </div>
              <div className="Referrals__column">
                <List>
                  <ListItem chevron href={GOLD_PROGRAM_TELEGRAM}>
                    {t('invite_stats_item_telegram')}
                  </ListItem>
                  <ListItem
                    onClick={handleShowLinks}
                    indicator={pagination['statsTable'].length.toString()}
                    chevron
                  >
                    {t('referrals_stats_item_links')}
                  </ListItem>
                </List>
                <Button onClick={handleCreateLink} iconRight={<IcPlusCircle28 />}>
                  {t('referrals_create_link_button')}
                </Button>
              </div>
            </div>
            <div className="Referrals__title">{t('referrals_subtitle_stats')}</div>
            <div className="Referrals__columns">
              <div className="Referrals__column">
                <Select
                  classNamePrefix="Select"
                  isSearchable
                  isMulti
                  value={linkFilterValue}
                  onChange={(values) => {
                    setLinkFilter(values.map((value) => value.value));
                  }}
                  options={linkFilterOptions}
                  placeholder={t('referrals_filter_placeholder')}
                  noOptionsMessage={() => t('edit_countries_not_found')}
                  components={{
                    DropdownIndicator: () => <IcDropdown16 />,
                  }}
                />
              </div>
              <div className="Referrals__column">
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(range) => {
                    dispatch(inviteActions.setStateDateRange(range));
                  }}
                  isDesktop={isDesktop}
                  isClearable={true}
                />
              </div>
            </div>
            {preparedStats.length ? (
              <Table
                enableSorting
                data={preparedStats}
                columns={columns}
                className="Referrals__table"
              />
            ) : stats.stats.length ? (
              <Placeholder
                title={t('referrals_empty_stat_placeholder_title')}
                actions={
                  <Button onClick={() => setLinkFilter([])}>
                    {t('referrals_empty_stat_placeholder_button')}
                  </Button>
                }
              >
                {t('referrals_empty_stat_placeholder_desc')}
              </Placeholder>
            ) : (
              <Placeholder
                title={t('referrals_create_link_placeholder_title')}
                actions={
                  <Button mode="secondary" onClick={() => showModal('create_link')}>
                    {t('referrals_create_link_button')}
                  </Button>
                }
              >
                {t('referrals_create_link_placeholder_desc')}
              </Placeholder>
            )}
          </div>
        )}
        {pagination['statsTable'].isLoading && <Loader mode="additional" />}
      </ScrollView>
    </>
  );
};
